// <algolia-recommend> - a custom, hand-rolled replacement for the official
// Algolia AI Search & Discovery app's "Recommend" block (see
// templates/*.algolia-test.json for the disabled app block this was modeled
// on - same settings vocabulary: model/maxRecommendations/minRecommendations
// /threshold/showVendor), built because the app block's own output can't be
// restyled to match this theme and ships no layout controls.
//
// Reuses the exact same building blocks as the collection grid/search
// results (algolia-client.js's AlgoliaClient, algolia-product-tile.js's
// renderProductTile) so a shopper sees one consistent product tile
// everywhere - just against Algolia's Recommend API
// (POST /1/indexes/*/recommendations) instead of Search.
import { AlgoliaClient } from "./algolia-client.js";
import { renderProductTile } from "./algolia-product-tile.js";
import { d as addItemById, a as contexts } from "./cart-cce84c00.js";
import { E as EVENTS } from "./events-58bc9098.js";

// Fires a Shopify Customer Events "custom event" - the supported theme-side
// hook into Web Pixels (window.Shopify.analytics.publish -> any pixel's
// analytics.subscribe("algolia_recommend_*", ...)). This is how these
// clicks/views/conversions reach GTM: see pixels/stape extended events.txt,
// which subscribes to the three event names below and relays them into the
// GTM web container's dataLayer via the same postMessage bridge it already
// uses for product_viewed/add_to_cart/etc - same single, consent-gated path
// every other Algolia signal on this site goes through (see
// algolia-client.js's publishAlgoliaEvent comment). Deliberately
// best-effort - a shopper without analytics consent, or a store with no
// pixel subscribed yet, just gets a no-op here.
function publishGtmEvent(name, payload) {
  try {
    window.Shopify?.analytics?.publish?.(name, payload);
  } catch (e) {
    // Shopify.analytics is unavailable (e.g. theme editor preview) - no-op.
  }
}

function hitToGtmItem(hit, position) {
  return {
    objectID: hit.objectID,
    title: hit.title,
    vendor: hit.vendor,
    price: hit.price,
    position,
  };
}

// The anonymous user token search-insights persists when useCookie is on -
// the same token GTM's Insights tags attach to events. Reading it here lets
// the Recommend request carry a matching userToken so the request and its
// downstream events line up. Best-effort: on a first visit, before Insights
// has initialised, the cookie isn't there yet - we omit userToken and rely on
// the queryID alone to link events back to the recommendation.
function getInsightsUserToken() {
  try {
    const m = document.cookie.match(/(?:^|;\s*)_ALGOLIA=([^;]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  } catch (e) {
    return null;
  }
}

// Per-session cache of Recommend responses, keyed by request signature
// (index|model|objectId|facet). The homepage trending/related rails - and every
// PDP's related-products - otherwise fire the SAME getRecommendations request on
// every page view; caching the raw response (hits + queryID) for the session
// means each distinct recommendation is fetched once, not once per view. The
// exclude filter (cart contents) is still applied fresh per call on the cached
// raw hits, and the "viewed" impression still fires on every real view, so
// analytics are unaffected - only the redundant network requests are removed.
const RECO_CACHE_TTL_MS = 30 * 60 * 1000;

function recoCacheKey(indexName, model, objectId, facetName, facetValue) {
  return "algolia_reco:" + [indexName, model, objectId || "", facetName || "", facetValue || ""].join("|");
}

function readRecoCache(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const c = JSON.parse(raw);
    if (!c || typeof c.ts !== "number" || Date.now() - c.ts > RECO_CACHE_TTL_MS) return null;
    return c;
  } catch (e) {
    return null;
  }
}

function writeRecoCache(key, hits, queryID) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ hits: hits, queryID: queryID, ts: Date.now() }));
  } catch (e) {}
}

// --- Engagement gate --------------------------------------------------------
// Recommend usage was dominated by loads that never interact - bots that render
// the page, prefetch/prerender, instant bounces - visible as a huge request
// count against ~0% CTR/CVR. Every rail on this site sits below the fold, so a
// real shopper always produces a scroll or pointer/touch move before they could
// ever see (let alone click) one; a headless renderer usually produces none. So
// hold each Recommend fetch until the first genuine interaction on the page,
// then let the existing viewport/eager logic run exactly as before. One-shot
// and passive: a session that does interact pays nothing and behaves normally;
// a session that never interacts simply never fires a Recommend request.
let userEngaged = false;
let engagementBound = false;
const engagementWaiters = [];
const ENGAGEMENT_EVENTS = ["pointerdown", "pointermove", "wheel", "touchstart", "keydown", "scroll"];

function onEngaged() {
  if (userEngaged) return;
  userEngaged = true;
  ENGAGEMENT_EVENTS.forEach((t) => window.removeEventListener(t, onEngaged, true));
  while (engagementWaiters.length) engagementWaiters.shift()();
}

function whenEngaged(fn) {
  if (userEngaged) {
    fn();
    return;
  }
  if (!engagementBound) {
    engagementBound = true;
    ENGAGEMENT_EVENTS.forEach((t) => window.addEventListener(t, onEngaged, { capture: true, passive: true }));
  }
  engagementWaiters.push(fn);
}

class AlgoliaRecommend extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const eventOptions = { signal: this.controller.signal };

    const configEl = this.querySelector("[js-algolia-recommend-config]");
    if (!configEl) return;
    this.config = JSON.parse(configEl.textContent);

    if (!this.config.appId || !this.config.searchKey || !this.config.indexName) {
      console.warn("[algolia-recommend] missing Algolia app ID/search key/index name - not rendering.");
      this.classList.add("hidden");
      return;
    }

    // Anchor-based models (everything except trending-items) need the
    // current product's variant objectID - absent when this section is
    // placed somewhere other than a product template, or before a variant
    // is resolved. Rather than render an empty/broken widget, hide it, same
    // as <recommended-products> does when Shopify's own recommendation
    // endpoint comes back empty.
    if (this.config.requiresObjectId && !this.config.objectId) {
      this.classList.add("hidden");
      return;
    }

    // Delegated on `this` (the root custom element, never replaced - see
    // renderTiles) rather than the slider track itself, which does get
    // replaced wholesale once real tiles arrive.
    this.client = new AlgoliaClient({ appId: this.config.appId, searchKey: this.config.searchKey });
    // Recommend clicks are tracked via the product link's algolia_rec/oid/pos
    // params + the GTM "Wait for Tags" link-click tag (see renderTiles), so no
    // click listener is bound here - a Web-Pixel publish on click lost the event
    // to the page navigating away. Quick-add (converted) stays a delegate below
    // because add-to-cart doesn't navigate.
    this.bindQuickAddDelegate(eventOptions);
    this.bindClickAttribution(eventOptions);

    // Cart placements fetch eagerly: the cart is a deliberate, low-frequency
    // surface (unlike below-the-fold PDP/home rails), and the drawer renders
    // from a <template> that clones on open - a viewport IntersectionObserver
    // can't reliably intersect content in that scroll container, so the lazy
    // path would never fire. Everywhere else stays lazy to avoid a request on
    // every home/product view.
    // Cart placements fetch immediately on connect. The drawer's markup lives
    // in a <template> that only clones into the live DOM when the drawer opens
    // (see sections/cart-drawer.liquid) - so "on connect" there already means
    // "on open", a deliberate action that never fires at page load. The cart
    // page connects on its own navigation. Neither is the bot-driven, below-the-
    // fold home/PDP load the engagement gate exists for, so the eager path is
    // NOT gated (gating it would also miss the opening click that clones the
    // template, and the rail would never load). Only the lazy path is gated.
    if (this.config.eager) {
      this.fetchRecommendations();
    } else {
      this.observeAndFetch();
    }
  }

  // Defer the Recommend API request until the shopper actually scrolls the
  // widget into view AND has interacted with the page (whenEngaged). These
  // sections are almost always below the fold (PDP cross-sells, home-page
  // trending/related rows), so fetching on connect fired an Algolia request on
  // every home-page and product view whether or not the shopper ever reached
  // it. The observer now uses a 0 rootMargin (no early-prefetch margin): on a
  // short PDP a generous margin counted the rail as "near" the viewport at page
  // load and fired straight away, so requiring the rail to genuinely enter the
  // viewport means a short page that never gets scrolled never fetches. Falls
  // back to an engagement-gated immediate fetch where IntersectionObserver is
  // unavailable.
  observeAndFetch() {
    if (!("IntersectionObserver" in window)) {
      whenEngaged(() => this.fetchRecommendations());
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        whenEngaged(() => this.fetchRecommendations());
      },
      { rootMargin: "0px" },
    );
    observer.observe(this);
    this.fetchObserver = observer;
  }

  async fetchRecommendations() {
    try {
      let hits = [];

      // Primary model. Anchor models (bought-together / related-products /
      // looking-similar) need an objectID; trending-items doesn't. In the cart
      // the anchor is the last-added line, which is absent when the cart is
      // empty - so with no anchor we skip straight to the fallback below.
      if (this.config.objectId || this.config.model === "trending-items") {
        hits = await this.runModel(this.config.model, this.config.modelLabel, this.config.objectId);
      }

      // Fallback model (e.g. cart FBT -> Trending Items): used when the primary
      // couldn't run (no anchor) or came back with too few hits after removing
      // the seed and anything already in the cart, so the rail never renders
      // empty or half-full.
      if (hits.length < this.config.minRecommendations && this.config.fallbackModel) {
        hits = await this.runModel(this.config.fallbackModel, this.config.fallbackModelLabel, null);
      }

      if (hits.length < this.config.minRecommendations) {
        // Most common causes: this objectID genuinely has no recommendations
        // yet (too little Algolia event data for this model), the objectID
        // doesn't match the index's record, or (cart) everything recommended is
        // already in the basket and got excluded.
        console.warn(
          `[algolia-recommend] only ${hits.length} usable hit(s) (model "${this.config.model}"${
            this.config.fallbackModel ? ` / fallback "${this.config.fallbackModel}"` : ""
          }, objectID: ${this.config.objectId || "none"}, index: ${this.config.indexName}), minRecommendations is ${
            this.config.minRecommendations
          } - hiding section.`,
        );
        this.classList.add("hidden");
        return;
      }

      this.hits = hits;
      this.renderTiles(hits);
      this.observeViewed(hits);
      this.classList.remove("loading");
    } catch (e) {
      console.error("[algolia-recommend] recommendations request failed", e);
      this.classList.add("hidden");
    }
  }

  // Runs a single Recommend model and returns its hits, minus the seed product
  // and any excluded objectIDs (e.g. items already in the cart). Records the
  // model that actually produced the shown tiles (this.activeModel/Label) so
  // click/view/conversion analytics carry the right per-model event name even
  // when the fallback ran, and captures this.queryID from that model's response
  // (clickAnalytics) for query-backed click/conversion attribution.
  async runModel(model, modelLabel, objectId) {
    const request = {
      indexName: this.config.indexName,
      model,
      maxRecommendations: this.config.maxRecommendations,
      threshold: this.config.threshold,
    };
    if (objectId) request.objectID = objectId;
    if (model === "trending-items" && this.config.facetName && this.config.facetValue) {
      request.facetName = this.config.facetName;
      request.facetValue = this.config.facetValue;
    }
    // clickAnalytics makes the Recommend response return a queryID - REQUIRED
    // for Recommend analytics. Without it, click/conversion events can't be
    // attributed and CTR/CVR stay empty (Algolia, "Send events for Recommend
    // analytics"). Recommend reuses the Search event model, so those events go
    // to clickedObjectIDsAfterSearch / convertedObjectIDsAfterSearch carrying
    // this queryID. (View events are tracked without a queryID.)
    request.queryParameters = { clickAnalytics: true };
    const userToken = getInsightsUserToken();
    if (userToken) request.queryParameters.userToken = userToken;

    // Reuse a cached response for the same request within the session instead of
    // re-hitting Algolia on every page view (see recoCacheKey above). Key is the
    // request signature only - NOT the cart excludes - so the exclusion filter
    // still runs fresh below on the cached raw hits.
    const cacheKey = recoCacheKey(this.config.indexName, model, objectId, request.facetName, request.facetValue);
    let rawHits;
    let queryID;
    const cached = readRecoCache(cacheKey);
    if (cached) {
      rawHits = cached.hits || [];
      queryID = cached.queryID || null;
    } else {
      const [result] = await this.client.getRecommendations([request]);
      rawHits = result?.hits || [];
      queryID = result?.queryID || null;
      writeRecoCache(cacheKey, rawHits, queryID);
    }

    // Exclude the seed and anything already in the cart (config.excludeObjectIds
    // - variant IDs, matching Algolia objectIDs), applied fresh on the (possibly
    // cached) raw hits since cart contents change between calls. Compared as
    // strings since Algolia objectIDs are always strings.
    const exclude = new Set([objectId, ...(this.config.excludeObjectIds || [])].filter(Boolean).map(String));
    const hits = rawHits
      .filter((hit) => !exclude.has(String(hit.objectID)))
      .slice(0, this.config.maxRecommendations);

    // Commit this model's identity/queryID only when its hits are the ones
    // we'll actually show - it cleared the threshold, or it's the fallback
    // (last resort). Stops a too-thin primary from stamping its queryID/label
    // onto tiles the fallback ends up rendering.
    if (hits.length >= this.config.minRecommendations || model === this.config.fallbackModel) {
      this.queryID = queryID;
      this.activeModel = model;
      this.activeModelLabel = modelLabel;
    }
    return hits;
  }

  // scroll-slider.js (and scroll-slider-with-scroll-indicator.js, when the
  // scroll indicator is enabled) each cache their own child element
  // references exactly once, in connectedCallback, and never re-query them -
  // there's no public "refresh" API. Writing real tiles into the existing
  // .scroll-slider__slider via innerHTML would leave those caches pointing
  // at detached skeleton nodes forever (dead scroll/resize handling, no
  // pagination). Cloning the slider root (whichever one is present - see
  // algolia-recommend.liquid's .algolia-recommend__slider-anchor wrapper),
  // filling in the *clone's* track, then swapping it in wholesale forces a
  // full disconnect/reconnect of every custom element inside it, so they all
  // re-query fresh against the real tiles. Elements outside this subtree
  // (the nav buttons, this root element) are never touched.
  renderTiles(hits) {
    const anchor = this.querySelector(".algolia-recommend__slider-anchor");
    const oldRoot = anchor?.firstElementChild;
    if (!oldRoot) return;

    const newRoot = oldRoot.cloneNode(true);
    // The track is normally a descendant of the cloned root (scroll-slider-
    // component wrapper), but if the first child ends up being the track itself
    // - or anything without it - querySelector alone would miss it and throw.
    const track = newRoot.matches?.(".scroll-slider__slider")
      ? newRoot
      : newRoot.querySelector(".scroll-slider__slider");
    if (!track) {
      console.warn("[algolia-recommend] slider track (.scroll-slider__slider) not found - not rendering tiles.");
      return;
    }
    // Stamp each tile's product link with the Recommend click-attribution params
    // (algolia_rec/oid/pos). The GTM "Wait for Tags" link-click tag reads them
    // and sends clickedObjectIDs before navigation - reliable, unlike the old
    // Web-Pixel publish which lost the click to the PDP navigating away.
    const recommendEvent = `${this.activeModelLabel || this.config.modelLabel || "Recommend"} Clicked`;
    track.innerHTML = hits
      .map((hit, i) => renderProductTile(hit, this.config, i, { recommendEvent, queryID: this.queryID }))
      .join("");
    oldRoot.replaceWith(newRoot);
  }

  reportViewed(hits) {
    publishGtmEvent("algolia_recommend_viewed", {
      model: this.activeModel || this.config.model,
      modelLabel: this.activeModelLabel || this.config.modelLabel,
      sourceObjectId: this.config.objectId,
      items: hits.map((hit, i) => hitToGtmItem(hit, i + 1)),
    });
  }

  // Recommendations fetch/render as soon as the widget connects, regardless
  // of scroll position (the section is frequently below the fold - PDP
  // cross-sells, footer-adjacent trending rows, etc.) - reporting "viewed"
  // at that same moment counted impressions the shopper never actually saw.
  // Deliberately a one-shot IntersectionObserver (disconnects after the
  // first trigger) rather than something re-firing on every scroll in/out -
  // Algolia's Insights "viewed" event means "shown once", not "currently
  // visible".
  observeViewed(hits) {
    const observer = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return;
      observer.disconnect();
      this.reportViewed(hits);
    });
    observer.observe(this);
    this.viewedObserver = observer;
  }

  // Publishes the Recommend queryID + objectID for a product the shopper is
  // about to add (quick-add) or navigate to (tile click). The attribution pixel
  // (Pixels/algolia-pixel.txt) records this into its click store, so the
  // subsequent product_added_to_cart carries this queryID into the cart's
  // algolia_object_ids attribute -> order -> server purchasedObjectIDsAfterSearch,
  // exactly like a search-driven purchase. This is what fills Recommend's
  // Revenue / AOV / Purchase-rate. No-op without a queryID (nothing to attribute).
  recordAddIntent(variantId, hit) {
    if (!this.queryID || !variantId) return;

    // Write the Recommend queryID into the same sessionStorage query_map the
    // search/collection grid uses (algolia-product-tile.js storeQueryAttribution),
    // so a Recommend quick-add is picked up by the GTM path: query_map -> GA4
    // config param algolia_query_id -> server store. A Recommend click-through
    // doesn't need this (its link carries ?algolia_qid=, which the GTM
    // sessionStorage tag already reads), but a quick-add never navigates, so
    // nothing else records its queryID for the GTM path.
    try {
      const raw = sessionStorage.getItem("algolia_query_map");
      const map = raw ? JSON.parse(raw) : {};
      map[String(variantId)] = { queryID: this.queryID, index: "", ts: Date.now() };
      const keys = Object.keys(map);
      if (keys.length > 50) {
        keys.sort((a, b) => map[a].ts - map[b].ts);
        keys.slice(0, keys.length - 50).forEach((k) => delete map[k]);
      }
      sessionStorage.setItem("algolia_query_map", JSON.stringify(map));
    } catch (e) {}

    publishGtmEvent("algolia_recommend_add_intent", {
      objectID: String(variantId),
      productId: hit?.product_id != null ? String(hit.product_id) : null,
      queryID: this.queryID,
    });
  }

  // Seeds attribution when a shopper clicks THROUGH a recommended tile to its
  // PDP (the other way a recommended product reaches the cart, besides quick-
  // add). Best-effort: the publish races the navigation, but the pixel's click
  // store persists across pages, so a delivered record still lets the eventual
  // add-to-cart on the PDP carry this queryID to the order. The Insights click
  // itself is unaffected - that rides the link's algolia_rec/qid params, not
  // this publish. Quick-add clicks are skipped here (handled with their own,
  // reliably-timed recordAddIntent above).
  bindClickAttribution(eventOptions) {
    this.addEventListener(
      "click",
      (event) => {
        if (event.target.closest("[js-algolia-quick-add]")) return;
        if (!event.target.closest("a[href]")) return;
        const tile = event.target.closest("[data-algolia-objectid]");
        if (!tile) return;
        const position = Number(tile.dataset.algoliaPosition);
        const hit = position ? this.hits?.[position - 1] : null;
        this.recordAddIntent(tile.dataset.algoliaObjectid, hit);
      },
      eventOptions,
    );
  }

  // Deliberately a separate, small delegate rather than importing
  // algolia-product-tile.js's bindQuickAddDelegate: that one is hardwired to
  // look for an ancestor <algolia-collection-product-grid> and fire a
  // queryID-carrying GTM event - neither applies here (this element has no
  // queryID, and needs its own "algolia_recommend_converted" event instead).
  // Cart-adding logic itself is duplicated rather than parameterized, same
  // "small helpers duplicated per file" tradeoff already made between
  // algolia-collection-grid.js and algolia-product-tile.js.
  bindQuickAddDelegate(eventOptions) {
    this.addEventListener(
      "click",
      (event) => {
        const button = event.target.closest("[js-algolia-quick-add]");
        if (!button || button.disabled) return;

        const tile = button.closest("[data-algolia-objectid]");
        const position = tile ? Number(tile.dataset.algoliaPosition) : null;
        const hit = position != null ? this.hits?.[position - 1] : null;

        // Seed purchase attribution BEFORE the add fires: the /cart/add.js
        // round-trip gives the attribution pixel time to record this queryID
        // into its click store, so the product_added_to_cart it receives moments
        // later carries the Recommend queryID to the order.
        this.recordAddIntent(button.dataset.variantId, hit);

        button.disabled = true;
        const originalLabel = button.textContent;
        button.textContent = "...";

        // addItemById()'s own promise resolves normally even on a 422 (sold
        // out, invalid/stale variant, etc.) - it never rejects, it just
        // dispatches a bubbling QUICK_ADD_ERROR event on the target element
        // passed in (cart-cce84c00.js's _handleError). The native
        // <product-item-quick-shopping> flow listens for that event to show
        // an inline error; this plain button has no such wrapper, so
        // without this listener a failed add was indistinguishable from a
        // successful one here - button reset, nothing in the cart, no error
        // visible anywhere.
        let addErrorMessage = null;
        const handleAddError = (errorEvent) => {
          addErrorMessage = errorEvent.detail?.errorMessage || "Couldn't add to cart";
        };
        button.addEventListener(EVENTS.QUICK_ADD_ERROR, handleAddError, { once: true });

        addItemById(button.dataset.variantId, 1, contexts.productItem, button)
          .then(() => {
            if (addErrorMessage) {
              console.error("[algolia-recommend] add to cart failed:", addErrorMessage);
              return;
            }

            window.dispatchEvent(new CustomEvent(EVENTS.CART_DRAWER_OPENING));

            if (tile) {
              publishGtmEvent("algolia_recommend_converted", {
                model: this.activeModel || this.config.model,
                modelLabel: this.activeModelLabel || this.config.modelLabel,
                sourceObjectId: this.config.objectId,
                // queryID from the Recommend response - sent to
                // convertedObjectIDsAfterSearch so the conversion is attributed
                // to this recommendation. null if the fetch returned no queryID.
                queryID: this.queryID || null,
                item: hit ? hitToGtmItem(hit, position) : { objectID: tile.dataset.algoliaObjectid, position },
              });
            }
          })
          .catch((e) => {
            console.error("[algolia-recommend] add to cart failed", e);
          })
          .finally(() => {
            button.removeEventListener(EVENTS.QUICK_ADD_ERROR, handleAddError);
            button.disabled = false;
            if (addErrorMessage) {
              // The button is a fixed-width, white-space:nowrap pill sized
              // for "Add to cart" - Shopify's real error text (often a full
              // sentence) just overflows it. Show a short, constant label
              // here; the actual message is already in the console.error
              // above for whoever's debugging.
              button.textContent = "Unavailable";
              setTimeout(() => {
                button.textContent = originalLabel;
              }, 2500);
              return;
            }
            button.textContent = originalLabel;
          });
      },
      eventOptions,
    );
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.fetchObserver?.disconnect();
    this.viewedObserver?.disconnect();
  }
}

if (!customElements.get("algolia-recommend")) {
  customElements.define("algolia-recommend", AlgoliaRecommend);
}